import React from 'react';
import fr from 'facet-rest';
import {
  FDBadge,
  FDSecondaryActionIconButton, FDSendButtonGroup, FDStack, FDTextInput,
} from '@facet-wealth/design';
import RichTextEditor from 'Components/UI/RichTextEditor';
import {
  useEmailTabContext,
} from 'Components/AdvisorApp/Viewport/MissionControl/Page/Versions/Default/Tabs/Email/EmailView/EmailTabContext';
import { openModal } from 'Helpers/ModalHelper';
import modalConsts from 'Consts/ModalNames';
import useUserContext from 'Context/UserContext';
import TemplateOptionsMenu from 'Components/UI/TemplateOptionsMenu';
import { TEMPLATE_TYPES } from 'Components/UI/TemplateOptionsMenu/utils';
import LoadingOverlay from 'Components/UI/LoadingOverlay/LoadingOverlay';
import { pick } from 'Helpers/Affordances';
import RichTextEditorCustomContextMenu from 'Components/UI/RichTextEditorCustomContextMenu';
import { dataAttrs } from 'Helpers/Testing';
import useEventTrackingLogEvent from 'EventTracking/useEventTrackingLogEvent';
import {
  ReadOnlyView,
} from 'Components/AdvisorApp/Viewport/MissionControl/Page/Versions/Default/Tabs/Email/ReadOnlyView';
import PropTypes from 'prop-types';
import {
  ATTACHMENTS_SECTION, MEMBER_TASKS_SECTION, PLANNER_TASKS_SECTION,
} from 'Components/AdvisorApp/Viewport/MissionControl/Page/Versions/Default/Tabs/Email/EmailView/Sections';
import RestoreGeneratedDraftOption
  from 'Components/UI/EmailForm/RestoreGeneratedDraftOption/RestoreGeneratedDraftOption';
import { SectionPaper } from '../SectionPaper';

export const EmailEditorSection = ({ setCurrentSection }) => {
  const {
    agendaResource,
    content,
    handleUpdateContent,
    subject,
    handleUpdateSubject,
    handleRestoreGeneratedContent,
    handleSaveDraft,
    attachmentCollection,
    handleInsertTemplateToken,
    handleInsertTemplate,
    meetingTemplateCollection,
    sendingEmail,
    meetingResource,
    householdResource,
    isSendingDisabled,
    handleClickSendToClient,
    handleClickScheduleSend,
    selectedClientActionItemMap,
    selectedPlannerActionItemMap,
  } = useEmailTabContext();
  const { agentResource } = useUserContext();
  const { logEvent } = useEventTrackingLogEvent({
    nodeName: 'Email Tab',
    addUseEventTrackingHookProps: true,
  });

  const memberTaskCount = Object.keys(selectedClientActionItemMap).length;
  const plannerTaskCount = Object.keys(selectedPlannerActionItemMap).length;
  const attachmentCount = attachmentCollection?.resources().length();

  const disableMessageSend = meetingResource.withRelationship(fr.relationships.data.name,
    () => true,
    () => isSendingDisabled);

  const onClickPreview = async () => {
    await handleSaveDraft();
    openModal(modalConsts.clientMeetingPreview, [
      { meetingId: meetingResource.id() },
    ]);
  };

  const onClickSchedule = async () => {
    await handleClickScheduleSend();
  };

  const EmailEditor = pick(agentResource, {
    [fr.relationships.notesContextMenu.name]: RichTextEditorCustomContextMenu,
  }, RichTextEditor);

  // Do not allow sending of an agenda message for PM1 & PM2 meetings that haven't begun yet
  const EmailView = meetingResource.withRelationship(fr.relationships.automatedPm1Pm2MeetingAgenda.name,
    () => (
      <ReadOnlyView
        meetingResource={meetingResource}
        householdResource={householdResource}
        className={undefined}
      />
    ),
    () => (
      <>
        <FDTextInput
          fullWidth
          label={'Subject'}
          value={subject}
          onChange={(evt) => handleUpdateSubject(evt.target.value)}
          {...dataAttrs('subject-input-field')}
        />
        <EmailEditor
          value={content}
          onChange={(v) => handleUpdateContent(v)}
          size={'full'}
          dataTestAttributes={dataAttrs('email-form-ckeditor')}
        />
      </>
    ));
  const TemplateOptionsMenuButton = meetingResource.withRelationship(fr.relationships.automatedPm1Pm2MeetingAgenda.name,
    () => null,
    () => (
      <TemplateOptionsMenu
        templates={meetingTemplateCollection}
        onInsertTemplate={template => {
          handleInsertTemplate(template);
        }}
        onInsertToken={(token) => handleInsertTemplateToken(token)}
        templateDraftContent={content}
        templateSubject={subject}
        templateType={TEMPLATE_TYPES.messageMeetingCombo}
        confirmBeforeInsert
      />
    ));

  return (
    <>
      {sendingEmail && <LoadingOverlay />}
      <SectionPaper height={'100%'} flex={1}>
        <FDStack direction={'column'} spacing={2} height={'100%'} flex={1} justifyContent={'flex-end'}>
          {EmailView}
          <FDStack spacing={2} justifyContent={'space-between'} alignItems={'center'} width={'100%'}>
            <FDStack spacing={1} alignItems={'center'} wrap>
              {agendaResource.withRelationship(fr.relationships.history.name, (rel) => {
                return (
                  <FDSecondaryActionIconButton
                    icon={'accessTime'}
                    label={'History'}
                    disabled={sendingEmail}
                    onClick={(evt) => {
                      evt.preventDefault();
                      evt.stopPropagation();
                      openModal(modalConsts.historyFlyout, [
                        { title: btoa('Meeting Agenda History') },
                        { link: btoa(rel.link('related').url()) },
                      ]);
                    }}
                  />
                );
              }, () => null)}
              {TemplateOptionsMenuButton}
              <RestoreGeneratedDraftOption
                agentResource={agentResource}
                meetingAgendaResource={agendaResource}
                handleChange={handleRestoreGeneratedContent}
              />
              <FDSecondaryActionIconButton
                onClick={() => {
                  logEvent('Mission Control: Clicked + BNS Task');
                  openModal(modalConsts.tasksAndTodos);
                }}
                test="meeting-add-bns-task"
                label={'+ Add BNS Task'}
                disabled={sendingEmail}
                icon={'lightBulb'}
              />
              <FDBadge appearance={'standard'} value={attachmentCount}>
                <FDSecondaryActionIconButton
                  label={'Attachments'}
                  icon={'attachFile'}
                  disabled={sendingEmail}
                  onClick={() => setCurrentSection(ATTACHMENTS_SECTION)}
                />
              </FDBadge>
              <FDBadge appearance={'standard'} value={memberTaskCount}>
                <FDSecondaryActionIconButton
                  label={'Member tasks'}
                  icon={'taskMember'}
                  disabled={sendingEmail}
                  onClick={() => setCurrentSection(MEMBER_TASKS_SECTION)}
                />
              </FDBadge>
              <FDBadge appearance={'standard'} value={plannerTaskCount}>
                <FDSecondaryActionIconButton
                  label={'Planner tasks'}
                  icon={'taskPlanner'}
                  disabled={sendingEmail}
                  onClick={() => setCurrentSection(PLANNER_TASKS_SECTION)}
                />
              </FDBadge>
            </FDStack>
            <FDSendButtonGroup
              label={agendaResource.attr('sendLabel')}
              onClickSend={() => handleClickSendToClient()}
              previewLabel={'Preview'}
              onClickPreview={onClickPreview}
              scheduleLabel={'Schedule'}
              onClickSchedule={onClickSchedule}
              disabled={disableMessageSend}
            />
          </FDStack>
        </FDStack>
      </SectionPaper>

    </>
  );
};

EmailEditorSection.propTypes = {
  setCurrentSection: PropTypes.func.isRequired,
};
